import { useState, useEffect, useRef, Suspense } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Check, Menu } from 'lucide-react';
import confetti from 'canvas-confetti';
import { fillCopy } from '@arthelper/shared/acquisition/strategy';
import { useWelcomeContext } from './WelcomeContext';
import {
  STEP_COMPONENT_REGISTRY,
  preloadAllSteps,
  STEP_MAX_WIDTHS,
  DEFAULT_STEP_MAX_WIDTH,
  findFinaleStepCode,
  isCompletionStep,
} from './step-registry';
import StepDots from './components/StepDots';
import GiftBanner from './components/GiftBanner';
import Done from './steps/Done';
import WaitingDone from './steps/WaitingDone';
import { useAccessConversions, useWaiting, WAITLIST_URL } from './useWaiting';
import UserDropdown from '../../components/UserDropdown';
import { ME_QUERY } from '../../graphql/queries';

const RETURNING_AFTER_MS = 24 * 60 * 60 * 1000;

/** Signed up more than a day ago: the intro greets them back instead of introducing ArtHelper. */
function isReturning(createdAt: string | number | null | undefined) {
  if (!createdAt) {
    return false;
  }
  return Date.now() - new Date(createdAt).getTime() > RETURNING_AFTER_MS;
}

/** Which intro greets them: the waiting wizard's "build your profile", or the usual welcome. */
function introKeys(waiting: boolean, createdAt: string | number | null | undefined): string {
  if (waiting) {
    return 'waiting.intro_';
  }
  return isReturning(createdAt) ? 'intro.returning_' : 'intro.';
}

/** Only a fresh admission gets the moment, not an old account that still reads its `via`. */
const ADMITTED_FRESH_MS = 14 * 24 * 60 * 60 * 1000;

/** The ways past a restricted strategy's gate; `open` signup has nothing to celebrate. */
const ADMITTED_VIA = ['invite', 'referral_unlock', 'admin_release', 'time_release'] as const;
type AdmittedVia = (typeof ADMITTED_VIA)[number];

interface Admission {
  via: AdmittedVia;
  inviter: string | null;
}

// The home page headline's gradient stops, so the celebration is in the brand's own colors.
const CONFETTI = ['#f06ea7', '#ef6f6c', '#f5a55c', '#6dd2c4', '#86efac', '#22c55e'];

/**
 * How a new member got past a restricted strategy's gate — a friend's invite, a friend joining,
 * an admin letting them in (the application path too), or the wait running out. Null for open
 * signups and for anyone admitted more than two weeks ago.
 */
function admissionOf(access: ReturnType<typeof useWaiting>['access']): Admission | null {
  if (!access || access.state !== 'active' || !access.joinedAt) {
    return null;
  }
  const via = ADMITTED_VIA.find((value) => value === access.via);
  if (!via || Date.now() - new Date(access.joinedAt).getTime() > ADMITTED_FRESH_MS) {
    return null;
  }
  return { via, inviter: access.inviter?.name?.trim() || null };
}

/** What the intro promises. The waiting wizard builds a private profile, so it promises only that. */
function introFeatures(waiting: boolean): string[] {
  if (waiting) {
    return ['waiting.feature_profile', 'waiting.feature_work', 'waiting.feature_private'];
  }
  // Led by the loop the PMF survey says fans love: honest feedback → learning → words for the work.
  return ['intro.feature_feedback', 'intro.feature_learn', 'intro.feature_words', 'intro.feature_artists'];
}

export default function WelcomeWizard() {
  const { t } = useTranslation('welcome-onboarding');
  const { steps, stepIndex, currentStep, session, isAdvancing } = useWelcomeContext();
  const { data: meData } = useQuery(ME_QUERY);
  // Held on the waitlist: the strategy's waiting wizard (a private profile), or the waitlist page.
  const { waiting, allowed, access, loading: accessLoading } = useWaiting();
  useAccessConversions(access);
  useEffect(() => {
    if (!accessLoading && !allowed) window.location.assign(WAITLIST_URL());
  }, [accessLoading, allowed]);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [prevIndex, setPrevIndex] = useState(stepIndex);
  // Pre-step intro screen. Shown only on the very first render of step 0
  // when the user hasn't begun yet (no completed events). Once dismissed it
  // stays dismissed for the rest of this tab session.
  const [hasStartedFlow, setHasStartedFlow] = useState(false);
  const stepContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    preloadAllSteps();
  }, []);

  useEffect(() => {
    if (stepIndex > prevIndex) {
      setDirection('forward');
    } else if (stepIndex < prevIndex) {
      setDirection('back');
    }
    setPrevIndex(stepIndex);
    stepContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stepIndex, prevIndex]);

  if (!session) {
    return (
      <div className="ob-root">
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b7280',
          }}
        >
          Loading...
        </div>
      </div>
    );
  }

  /**
   * COMPLETION. Three endings coexist:
   * - a version ending on a FINALE_* step keeps the in-progress tree (the finale is
   *   already mounted; a root swap would remount it mid-`location.assign`);
   * - a version with COMPLETION_* steps keeps the step tree too — the outcome flipped
   *   at the last normal step, and `currentStepCode` is now walking the bonus lap;
   * - everything else (every pre-existing version) gets the original Done screen.
   */
  // The waiting wizard ends on "we'll let you know", never on the live-profile Done.
  if (waiting && access && session.outcome === 'COMPLETED') {
    return <WaitingDone access={access} />;
  }

  if (
    session.outcome === 'COMPLETED' &&
    !findFinaleStepCode(steps.map((step) => step.stepCode)) &&
    !isCompletionStep(session.currentStepCode)
  ) {
    return <Done />;
  }

  if (!currentStep) {
    return (
      <div className="ob-root">
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b7280',
          }}
        >
          No step available
        </div>
      </div>
    );
  }

  /**
   * Completion steps render OUTSIDE the AnimatePresence tree, the way Done always has.
   * The boundary hop (outcome flip + refetches + a lazy chunk) wedges `mode="wait"` —
   * observed live as the incoming step stuck at opacity 0 — and these steps are
   * full-screen cards that don't want the step-frame fade anyway.
   */
  if (isCompletionStep(currentStep.stepCode)) {
    const CompletionComponent = STEP_COMPONENT_REGISTRY[currentStep.stepCode as keyof typeof STEP_COMPONENT_REGISTRY];
    return (
      <div className="ob-root" style={{ display: 'flex', flexDirection: 'column' }}>
        {steps.length > 0 && <StepDots total={steps.length} currentIndex={stepIndex} />}
        <div className="ob-step-container">
          <div style={{ width: '100%', maxWidth: STEP_MAX_WIDTHS[currentStep.stepCode] ?? '720px' }}>
            <Suspense fallback={null}>{CompletionComponent ? <CompletionComponent /> : null}</Suspense>
          </div>
        </div>
      </div>
    );
  }

  const showIntro = stepIndex === 0 && !hasStartedFlow && (session.completedTotalCount ?? 0) === 0;
  const StepComponent = STEP_COMPONENT_REGISTRY[currentStep.stepCode as keyof typeof STEP_COMPONENT_REGISTRY];
  const showShellIndicator = !showIntro && steps.length > 0;
  const introPrefix = introKeys(waiting, meData?.user?.createdAt);
  // They got in past a restricted strategy: the intro is the "you're in" moment.
  const admission = admissionOf(access);

  const handleStartFlow = () => {
    setDirection('forward');
    setHasStartedFlow(true);
  };

  // Opacity-only fade: a transform here creates a containing block that breaks the fixed bottom nav.
  const animationVariants = {
    forward: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    back: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
  };

  return (
    <div
      className={`ob-root ob-root--with-user-menu${isAdvancing ? ' ob-root--advancing' : ''}`}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      {isAdvancing && (
        <div className="ob-advance-bar" role="progressbar" aria-label="Loading next step">
          <div className="ob-advance-bar-fill" />
        </div>
      )}
      <div className="fixed top-4 right-4 z-50">
        <UserDropdown hideItems={['blocked']} trigger={<Menu className="size-5 text-gray-600" />} />
      </div>
      {showShellIndicator && <StepDots total={steps.length} currentIndex={stepIndex} />}

      <div className="ob-step-container" ref={stepContainerRef}>
        {/* A gift-invite recipient's first screen confirms the gift landed (#4084). */}
        {stepIndex === 0 && <GiftBanner />}
        <AnimatePresence mode="wait">
          {showIntro ? (
            <motion.div
              key="intro"
              initial={animationVariants[direction].initial}
              animate={animationVariants[direction].animate}
              exit={animationVariants[direction].exit}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              style={{ width: '100%', maxWidth: '720px' }}
            >
              <div className="ob-welcome-intro">
                <div className="ob-welcome-intro-bg" aria-hidden />
                {admission && access ? (
                  <AdmittedIntro
                    admission={admission}
                    welcomeBody={access.copy.welcomeBody ?? ''}
                    profileCarried={Boolean(session.carriedStepCodes?.length)}
                  />
                ) : (
                  <>
                    <div className="ob-welcome-intro-eyebrow">
                      <span>{t(`${introPrefix}eyebrow`)}</span>
                    </div>
                    <h1 className="ob-welcome-intro-title">
                      <Trans i18nKey={`${introPrefix}title`} t={t} components={{ em: <em /> }} />
                    </h1>
                    <p className="ob-welcome-intro-subtitle">{t(`${introPrefix}subtitle`)}</p>
                  </>
                )}
                <button type="button" className="ob-welcome-intro-cta" onClick={handleStartFlow}>
                  <span className="ob-welcome-intro-cta-stack">
                    <span className="ob-welcome-intro-cta-main">
                      {t(admission ? 'admitted.cta' : `${introPrefix}cta`)}
                    </span>
                    <span className="ob-welcome-intro-cta-sub">{t('intro.cta_sub')}</span>
                  </span>
                  <span className="ob-welcome-intro-cta-arrow" aria-hidden>
                    →
                  </span>
                </button>
                <div className="ob-welcome-intro-strip">
                  {introFeatures(waiting).map((key) => (
                    <span key={key} className="ob-welcome-intro-tag">
                      <span className="ob-welcome-intro-tick" aria-hidden>
                        ✓
                      </span>
                      {t(key)}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={currentStep.stepCode}
              initial={animationVariants[direction].initial}
              animate={animationVariants[direction].animate}
              exit={animationVariants[direction].exit}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              style={{ width: '100%', maxWidth: STEP_MAX_WIDTHS[currentStep.stepCode] ?? DEFAULT_STEP_MAX_WIDTH }}
            >
              <Suspense fallback={null}>
                {StepComponent ? (
                  <StepComponent />
                ) : (
                  <div className="ob-step-card">
                    <p>Step not found</p>
                  </div>
                )}
              </Suspense>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/** Why they're in, in one line: the strategy's own welcome copy for an invite, ours otherwise. */
function reasonLine(admission: Admission, welcomeBody: string, t: (key: string) => string): string {
  if (admission.via === 'invite' && admission.inviter && welcomeBody) {
    return fillCopy(welcomeBody, { inviter: admission.inviter });
  }
  return t(`admitted.reason_${admission.via}`);
}

/**
 * The intro for anyone a restricted strategy let in: "You're in", who invited them (when a friend
 * did), why they're in, and a burst of confetti. It is the reason to start the wizard, so it
 * comes first.
 */
function AdmittedIntro({
  admission,
  welcomeBody,
  profileCarried,
}: {
  admission: Admission;
  welcomeBody: string;
  profileCarried: boolean;
}) {
  const { t } = useTranslation('welcome-onboarding');
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void confetti({ particleCount: 80, spread: 85, startVelocity: 30, origin: { y: 0.3 }, colors: CONFETTI });
    }, 300);
    return () => window.clearTimeout(timer);
  }, []);
  const inviter = admission.via === 'invite' ? admission.inviter : null;
  // An admin letting them in (the application path too) reads as an acceptance, not an invite.
  const accepted = admission.via === 'admin_release';

  return (
    <>
      <div className="ob-welcome-intro-eyebrow">
        <span>{t('admitted.eyebrow')}</span>
      </div>
      {inviter && (
        <p className="ob-invited-by" data-testid="welcome-invited-by">
          <span className="ob-invited-seal" aria-hidden>
            {(inviter[0] ?? '?').toUpperCase()}
          </span>
          {t('admitted.invited_by', { name: inviter })}
        </p>
      )}
      {accepted && (
        <p className="ob-invited-by" data-testid="welcome-accepted-by">
          <span className="ob-invited-seal" aria-hidden>
            <Check size={18} strokeWidth={3} />
          </span>
          {t('admitted.accepted_by')}
        </p>
      )}
      <h1 className="ob-welcome-intro-title">
        <Trans i18nKey="admitted.title" t={t} components={{ em: <em /> }} />
      </h1>
      <p className="ob-welcome-intro-subtitle" data-testid="welcome-admitted-reason">
        {reasonLine(admission, welcomeBody, t)} {t(profileCarried ? 'admitted.next_carried' : 'admitted.next')}
      </p>
    </>
  );
}
